import { alphabetically } from '@seabrookstudios/types';
import { DateTime } from 'luxon';

/**
 * @param {import("..").AdminOnlyPublicContact|undefined} profile
 * @returns {boolean}
 */
const preferredCurrencySet = (profile) =>
  profile ? !!profile.currencySettings.preferredCurrency : false;

/**
 * @param {import("..").ContactLocation} location
 */
const formatContactLocation = (location) => {
  if (!location.city) {
    return location.country;
  }

  return `${location.city}, ${location.country}`;
};

/**
 * @type {import("..").DesignerGameStatus[]}
 */
const ShowContracts = ['Signed', 'Published'];

/**
 * @param {import("..").Game} game
 * @returns {boolean}
 */
const showContract = (game) => ShowContracts.includes(game.status);

/**
 * @type {Record<import("..").Currency,null>}
 */
const AllCurrencies = {
  AUD: null,
  BGN: null,
  BRL: null,
  CAD: null,
  CHF: null,
  CNY: null,
  CZK: null,
  DKK: null,
  EUR: null,
  GBP: null,
  HKD: null,
  HUF: null,
  IDR: null,
  ILS: null,
  INR: null,
  ISK: null,
  JPY: null,
  KRW: null,
  MXN: null,
  MYR: null,
  NOK: null,
  NZD: null,
  PHP: null,
  PLN: null,
  RON: null,
  SEK: null,
  SGD: null,
  THB: null,
  TRY: null,
  USD: null,
  ZAR: null,
};

/**
 * @type {import("..").Currency[]}
 */
const SupportedCurrencies = /** @type {import("..").Currency[]} */ (
  Object.keys(AllCurrencies)
);

/**
 * @param {import("..").Currency} currency
 * @returns {import("..").Currency|undefined}
 */
const validateCurrency = (currency) => {
  return SupportedCurrencies.includes(currency) ? currency : undefined;
};

/**
 * @param {number} age
 */
const formatAge = (age) => {
  return age ? `${age}+` : undefined;
};

/**
 * @type {Record<import("..").DesignerGameStatus, string>}
 */
const StatusLables = {
  Idea: 'Concept',
  InDevelopmentEarly: 'In Development - Early',
  InDevelopmentMid: 'In Development - Mid',
  InDevelopmentLate: 'In Development - Late',
  PitchingNow: 'Pitching Now',
  Signed: 'Signed',
  Published: 'Published',
};

/**
 * @type {Record<import("..").DesignerGameStatus, string>}
 */
const StatusDescriptions = {
  Idea: 'A fresh idea, untested but exciting.',
  InDevelopmentEarly: 'Early tests with significant changes expected.',
  InDevelopmentMid: 'Basic systems bedded down, changes still expected.',
  InDevelopmentLate: 'Balancing and iteration.',
  PitchingNow: 'Actively pitching to publishers.',
  Signed: 'Contract signed with publisher.',
  Published: 'In the market or out of print.',
};

/**
 * @type {import("..").DesignerGameStatus[]}
 */
const DesignerGameStatuses = /** @type {import("..").DesignerGameStatus[]} */ (
  Object.keys(StatusLables)
);

/**
 * @typedef {object} GameSummarySection
 * @property {string} title
 * @property {import("..").DesignerGameStatus} status
 * @property {import("..").GameSummary[]} data
 */

/**
 * @type {Record<import("..").DesignerGameStatus, number>}
 */
const DesignerGameSort = {
  PitchingNow: 10,
  InDevelopmentLate: 20,
  InDevelopmentMid: 30,
  InDevelopmentEarly: 40,
  Idea: 50,
  Signed: 90,
  Published: 100,
};

/**
 * @param {GameSummarySection} a
 * @param {GameSummarySection} b
 */
const byStatus$1 = (a, b) => DesignerGameSort[a.status] - DesignerGameSort[b.status];

/**
 *
 * @param {import('..').GameSummary} a
 * @param {import('..').GameSummary} b
 */
const byStatusAndAlphabetically = (a, b) => {
  if (DesignerGameSort[a.status] === DesignerGameSort[b.status]) {
    return alphabetically('name')(a, b);
  }

  return DesignerGameSort[a.status] - DesignerGameSort[b.status];
};

/**
 * @param {import("..").GameSummary[]} games
 * @param {Record<import("..").DesignerGameStatus,boolean>} expanded
 * @returns {GameSummarySection[]}
 */
const sectionGames = (games, expanded) => {
  const sections = Array(DesignerGameStatuses.length)
    .fill(0)
    .map((_, i) => DesignerGameStatuses.toReversed()[i])
    .map((status) => {
      const data = games.filter((game) => game.status === status);

      return {
        title: `${StatusLables[status]} (${data.length})`,
        status,
        data,
      };
    })
    .filter((section) => section.data.length > 0)
    .map((section) => ({
      ...section,
      data: section.data.filter(() => expanded[section.status]),
    }));

  sections.sort(byStatus$1);

  return sections;
};

/**
 * @type {Record<import("..").PublisherGameStatus,string>}
 */
const PublisherGameStatusLabel = {
  ShortListed: 'Shortlisted',
  PitchReceived: 'Pitch Received',
  InProgress: 'In Progress',
  MaterialsRequested: 'Materials Requested',
  Withdrawn: 'Withdrawn',
  Declined: 'Declined',
  Signed: 'Signed',
  Lost: 'Lost',
  PitchSent: 'Pitch Sent',
  UnderConsideration: 'Under Consideration',
  InContractNegotiation: 'In Contract Negotation',
};

/**
 * @type {Record<import("..").PublisherGameStatus,string>}
 */
const PublisherGameStatusLabelShort = {
  ShortListed: 'Shortlisted',
  PitchReceived: 'Pitch Received',
  InProgress: 'In Progress',
  MaterialsRequested: 'Materials Requested',
  Withdrawn: 'Withdrawn',
  Declined: 'Declined',
  Signed: 'Signed',
  Lost: 'Lost',
  PitchSent: 'Pitch Sent',
  UnderConsideration: 'Under Consideration',
  InContractNegotiation: 'Contract Negotation',
};

/**
 * @type {Record<import('..').PublisherGameStatus,string>}
 */
const PublisherGameStatusDescription = {
  ShortListed: 'This publisher is on your submission shortlist.',
  PitchReceived: 'You have sent the game to the publisher.',
  InProgress: 'The publisher has started actively looking at the game.',
  MaterialsRequested: 'The publisher has requested more information.',
  Withdrawn: 'You have withdrawn the game from pitch,',
  Declined: 'The publisher has declined interest in the game.',
  Signed: 'The game has been signed by the publisher.',
  Lost: 'You have signed the game with another publisher.',
  PitchSent: "You've sent the pitch but have not heard anything back.",
  UnderConsideration: 'The publisher is very interested in the game.',
  InContractNegotiation: 'You have seen the initial contract and are working through it.',
};

/**
 * @type {Record<import('..').PublisherGameStatus, number>}
 */
const PublisherGameSort = {
  Signed: 1,
  InContractNegotiation: 5,
  MaterialsRequested: 10,
  UnderConsideration: 15,
  InProgress: 30,
  PitchReceived: 40,
  PitchSent: 45,
  ShortListed: 50,
  Withdrawn: 90,
  Lost: 90,
  Declined: 100,
};

/**
 * @param {import("..").PublisherGameStatus?} [status]
 * @returns {string}
 */
const formatPublisherStatus = (status) => {
  if (!status) {
    return 'UNKNOWN';
  }
  if (!PublisherGameStatusLabel[status]) {
    return status;
  }

  return PublisherGameStatusLabel[status].toUpperCase() || 'UNKNOWN';
};

/**
 * @param {number} timestamp
 * @returns {string}
 */
const getDaySubmitted = (timestamp = DateTime.now().toMillis()) => {
  const days = DateTime.now().diff(DateTime.fromMillis(timestamp), 'days').days;
  const daysRounded = Math.floor(days);

  return `Submitted: ${daysRounded === 1 ? '1 day ago' : `${daysRounded} days ago`}`;
};

/**
 * Returns how many days into the future a timestamp is
 * @param {number} timestamp
 * @returns {number}
 */
const daysFromNow = (timestamp) => {
  const days = DateTime.now().diff(DateTime.fromMillis(timestamp), 'days').days;
  const daysRounded = Math.round(Math.abs(days));

  return daysRounded;
};

/**
 * @param {number} timestamp
 * @returns {string}
 */
const getNextAction = (timestamp) => {
  const now = DateTime.now().toMillis();

  if (timestamp < now) {
    return 'Follow up due!';
  }

  const daysRounded = daysFromNow(timestamp);
  if (daysRounded === 0) {
    return 'Follow up due!';
  }

  if (daysRounded === 1) {
    return 'Follow up due tomorrow.';
  }

  return `Follow up due in ${daysRounded} days`;
};

/**
 * @param {number} timestamp
 * @returns {boolean}
 */
const isInFuture = (timestamp) => {
  const now = DateTime.now().toMillis();

  if (timestamp < now) {
    return false;
  }

  const daysRounded = daysFromNow(timestamp);
  if (daysRounded === 0) {
    return false;
  }

  return true;
};

/**
 * @type {import("..").PublisherGameStatus[]}
 */
const NoFollowUp = ['Declined', 'Lost', 'Withdrawn', 'Signed', 'ShortListed'];

/**
 * @param {import("..").DesignerPublisherGameView} publisherGame
 */
const formatFollowUp = (publisherGame) => {
  if (NoFollowUp.includes(publisherGame.status)) {
    return 'No follow up required.';
  }
  if (publisherGame.nextAction === 0) {
    return 'No follow up scheduled.';
  }

  return getNextAction(publisherGame.nextAction);
};

/**
 * @param {import("..").DesignerPublisherGameView} publisherGame
 */
const formatFollowUpWithDate = (publisherGame) => {
  if (NoFollowUp.includes(publisherGame.status)) {
    return 'No follow up required.';
  }

  if (publisherGame.nextAction === 0) {
    return 'No follow up scheduled. Click to add';
  }

  return `${getNextAction(publisherGame.nextAction)} Click to edit`;
};

/**
 * @param {import('..').DesignerPublisherGameView} a
 * @param {import('..').DesignerPublisherGameView} b
 */
const byStatus = (a, b) => PublisherGameSort[a.status] - PublisherGameSort[b.status];

/**
 * For each status, when we receive an update, how far into the future do we schedule the
 * next update? A value of null will not update the current follow up.
 * @type {Record<import('..').PublisherGameStatus, (number|null)>}
 */
const FollowUpStatusToDaysDefaults = {
  ShortListed: null,
  PitchSent: 28,
  PitchReceived: 28,
  InProgress: 28,
  MaterialsRequested: 3,
  UnderConsideration: 14,
  InContractNegotiation: 7,
  Declined: null,
  Signed: null,
  Lost: null,
  Withdrawn: null,
};

/**
 * @param {import('..').PublisherGameStatus} status
 * @returns {number|null}
 */
const nextFollowUpUsingStatus = (status) => {
  const days = FollowUpStatusToDaysDefaults[status];
  if (days === null) {
    return null;
  }

  const now = DateTime.now().toMillis();
  const msToAdd = 1000 * 60 * 60 * 24 * days;
  return now + msToAdd;
};

/**
 * @param {number} timestamp
 * @returns {boolean}
 */
const showAlarm = (timestamp) => {
  if (timestamp === 0) {
    return false;
  }
  if (timestamp < DateTime.now().toMillis()) {
    return true;
  }
  if (daysFromNow(timestamp) <= 3) {
    return true;
  }

  return false;
};

/**
 * @param {number} days
 */
const daysAgo = (days) => DateTime.now().minus({ days }).startOf('day').toMillis();

/**
 * @type {import("..").Filter<import("..").GameSummary>[]}
 */
const GamesListFilters = [
  {
    filter: (game) => (!game.nextAction ? false : showAlarm(game.nextAction)),
    label: 'DUE',
    description: 'Games with follow ups due.',
    count: 0,
    weight: 1000,
    startsOpen: true,
    icon: 'bell',
  },
  {
    filter: (game) => game.personalGoal === '2025' && !game.archived,
    label: '2025',
    description: `Signed in 2025! That's the goal.`,
    count: 0,
    weight: 500,
    startsOpen: true,
    icon: 'none',
  },
  {
    filter: (game) => ['PitchingNow'].includes(game.status) && !game.archived,
    label: 'PITCHING',
    description: 'Actively pitching to publishers.',
    count: 0,
    weight: 100,
    startsOpen: true,
    icon: 'none',
  },
  {
    filter: (game) => !game.archived && game.updated > daysAgo(14),
    label: 'RECENT',
    description: 'Games with some recent activity.',
    count: 0,
    weight: 75,
    startsOpen: false,
    icon: 'none',
  },
  {
    filter: (game) => ['Signed', 'Published'].includes(game.status) && !game.archived,
    label: 'DONE',
    description: 'Signed or published games.',
    count: 0,
    weight: 50,
    startsOpen: false,
    icon: 'none',
  },
  {
    filter: (game) =>
      ['InDevelopmentEarly', 'InDevelopmentMid', 'InDevelopmentLate'].includes(game.status) &&
      !game.archived,
    label: 'DESIGNING',
    description: 'Work in progress.',
    count: 0,
    weight: 10,
    startsOpen: false,
    icon: 'none',
  },
  {
    filter: (game) => !game.archived,
    label: 'ALL',
    description: 'All games, not archived.',
    count: 0,
    weight: 1,
    startsOpen: false,
    icon: 'none',
  },
  {
    filter: (game) => game.archived,
    label: 'ARCHIVE',
    description: 'Archived games.',
    count: 0,
    weight: 0,
    startsOpen: false,
    icon: 'archive',
  },
];

/**
 * @type {Record<import("..").TopicType, string>}
 */
const TopicTypeLabels = {
  General: 'General',
  PlayTest: 'Play Test',
  PublisherUpdates: 'Publisher Update',
  Rules: 'Rules',
  MeetingNotes: 'Meeting Notes',
  Variation: 'Variation',
  Diary: 'Design Diary',
};

/**
 * @type {Record<import("..").TopicType, string>}
 */
const TopicTypeDescriptions = {
  General: 'Uncategorised notes',
  PlayTest: 'Notes from a play test',
  PublisherUpdates: 'Your conversation with a publisher',
  Rules: 'A working document for the game rules',
  MeetingNotes: 'Notes from a meeting e.g. with a co-designer or advisor',
  Variation: 'An idea to explore that the game may end up going in',
  Diary: 'Your diary on the development of the game',
};

/**
 * Topics that the player can set. Publisher updates is excluded as we use this internally.
 * @type {import("..").TopicType[]}
 */
const TopicTypes = ['General', 'PlayTest', 'Rules', 'MeetingNotes', 'Variation', 'Diary'];

/**
 * Is this a valid topic that the player can set
 * @param {import("..").TopicType} type
 * @returns {import("..").TopicType|undefined}
 */
const validateTopicTypes = (type) => {
  return TopicTypes.includes(type) ? type : undefined;
};

/**
 * @type {Record<import("..").PlayTestAudience, string>}
 */
const PlayTestAudienceLabels = {
  Blind: 'Blind Test',
  FriendsFamily: 'Internal - Family & Friends',
  GameDesigners: 'Internal - Other Game Designers',
  Gamers: 'Local - Gamers',
  NonGamers: 'Local - Non-Gaming Folks',
  Solo: 'Internal - Designer playing all players',
  Publisher: 'Publisher',
};

/**
 * @type {Record<import("..").PlayTestAudience, string>}
 */
const PlayTestAudienceDescriptions = {
  Blind: 'Where the players learn the rules and play without designer support.',
  FriendsFamily: 'Among family and friends.',
  GameDesigners: 'Most of the players are other game designers.',
  Gamers: 'Most players are not new to board games',
  NonGamers: 'Most players are new to board games or this type of game.',
  Solo: 'Designer playing all players in the test.',
  Publisher: 'Play test with a publisher',
};

/**
 * @type {import("..").PlayTestAudience[]}
 */
const AudienceTypes = [
  'Blind',
  'NonGamers',
  'Gamers',
  'FriendsFamily',
  'GameDesigners',
  'Solo',
];

/**
 * @param {import("..").PlayTestAudience} type
 * @returns {import("..").PlayTestAudience|undefined}
 */
const validateAudienceTypes = (type) => {
  return AudienceTypes.includes(type) ? type : undefined;
};

/**
 * @type {Record<import("..").PlayTestMedium, string>}
 */
const PlayTestMediumLabels = {
  Digital: 'Online/Virtual testing',
  Physical: 'In-Person testing',
};

/**
 * @type {Record<import("..").PlayTestMedium, string>}
 */
const PlayTestMediumDescriptions = {
  Digital: 'Online testing using screentop.gg, Tabletopia, Table Top Simulator or similar',
  Physical: 'Players gathered around the table as how they would play the final product.',
};

/**
 * @type {import("..").PlayTestMedium[]}
 */
const PlayTestMediumTypes = ['Digital', 'Physical'];

/**
 * @param {import("..").PlayTestMedium} type
 * @returns {import("..").PlayTestMedium|undefined}
 */
const validatePlayTestMedium = (type) => {
  return PlayTestMediumTypes.includes(type) ? type : undefined;
};

/**
 * @type {Record<import("..").PlayTestMetadataComponentType, string>}
 */
const PlayTestMetadataComponentTypeLabels = {
  PART_OF_SCORE: 'Part of Score',
  PER_GAME: 'Per Game',
  PER_PLAYER: 'Per Player',
  SCORE: 'Final Score',
};

/**
 * @type {Record<import("..").PlayTestMetadataComponentType, string>}
 */
const PlayTestMetadataComponentTypeDescriptions = {
  PART_OF_SCORE: 'This value is part of a score but tracked separately.',
  PER_GAME: 'This attribute is tracked per game separately to the score e.g. number of trades',
  PER_PLAYER:
    'This attribute is tracked per player separately to the score e.g. do they have the longest road?',
  SCORE: 'The final score for the player.',
};

/**
 * @type {import("..").PlayTestMetadataComponentType[]}
 */
const PlayTestMetadataComponentTypeTypes = [
  'PART_OF_SCORE',
  'PER_GAME',
  'PER_PLAYER',
  'SCORE',
];

/**
 * @param {import("..").PlayTestMetadataComponentType} type
 * @returns {import("..").PlayTestMetadataComponentType}
 */
const validatePlayTestMetadataComponentType = (type) => {
  return PlayTestMetadataComponentTypeTypes.includes(type) ? type : 'PART_OF_SCORE';
};

/**
 * @type {Record<import("..").RoyaltyType, string>}
 */
const RoyaltyTypeLabels = {
  Royalty: 'Royalty',
  Advance: 'Advance',
  HoldingFee: 'Holding Fee',
  SigningBonus: 'Signing Bonus',
  Consulting: 'Consulting',
  Other: 'Other',
};

/**
 * @type {Record<import("..").RoyaltyType, string>}
 */
const RoyaltyTypeDescriptions = {
  Royalty: 'Royalties earned from actual or future game sales.',
  Advance: 'Upfront payment deducted from future royalties.',
  HoldingFee: 'A payment for exclusive access to the game pre-signing.',
  SigningBonus: 'Upfront payment separate from future royalties.',
  Consulting: 'A payment related to consulting services provided.',
  Other: 'Other',
};

/**
 * @type {import("..").RoyaltyType[]}
 */
const RoyaltyTypes = [
  'Royalty',
  'HoldingFee',
  'Advance',
  'SigningBonus',
  'Consulting',
  'Other',
];

/**
 * @param {import("..").RoyaltyType} type
 * @returns {import("..").RoyaltyType|undefined}
 */
const validateRoyaltyType = (type) => {
  return RoyaltyTypes.includes(type) ? type : undefined;
};

/**
 * @type {Record<import("..").Currency, object>}
 */
const CurrencyData = {
  USD: {
    symbol: '$',
    name: 'US Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'USD',
    namePlural: 'US dollars',
  },
  CAD: {
    symbol: 'CA$',
    name: 'Canadian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'CAD',
    namePlural: 'Canadian dollars',
  },
  EUR: {
    symbol: '€',
    name: 'Euro',
    symbolNative: '€',
    decimalDigits: 2,
    rounding: 0,
    code: 'EUR',
    namePlural: 'euros',
  },
  AUD: {
    symbol: 'AU$',
    name: 'Australian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'AUD',
    namePlural: 'Australian dollars',
  },
  BGN: {
    symbol: 'BGN',
    name: 'Bulgarian Lev',
    symbolNative: 'лв.',
    decimalDigits: 2,
    rounding: 0,
    code: 'BGN',
    namePlural: 'Bulgarian leva',
  },
  BRL: {
    symbol: 'R$',
    name: 'Brazilian Real',
    symbolNative: 'R$',
    decimalDigits: 2,
    rounding: 0,
    code: 'BRL',
    namePlural: 'Brazilian reals',
  },
  CHF: {
    symbol: 'CHF',
    name: 'Swiss Franc',
    symbolNative: 'CHF',
    decimalDigits: 2,
    rounding: 0.05,
    code: 'CHF',
    namePlural: 'Swiss francs',
  },
  CNY: {
    symbol: 'CN¥',
    name: 'Chinese Yuan',
    symbolNative: 'CN¥',
    decimalDigits: 2,
    rounding: 0,
    code: 'CNY',
    namePlural: 'Chinese yuan',
  },
  CZK: {
    symbol: 'Kč',
    name: 'Czech Republic Koruna',
    symbolNative: 'Kč',
    decimalDigits: 2,
    rounding: 0,
    code: 'CZK',
    namePlural: 'Czech Republic korunas',
  },
  DKK: {
    symbol: 'Dkr',
    name: 'Danish Krone',
    symbolNative: 'kr',
    decimalDigits: 2,
    rounding: 0,
    code: 'DKK',
    namePlural: 'Danish kroner',
  },
  GBP: {
    symbol: '£',
    name: 'British Pound Sterling',
    symbolNative: '£',
    decimalDigits: 2,
    rounding: 0,
    code: 'GBP',
    namePlural: 'British pounds sterling',
  },
  HKD: {
    symbol: 'HK$',
    name: 'Hong Kong Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'HKD',
    namePlural: 'Hong Kong dollars',
  },
  HUF: {
    symbol: 'Ft',
    name: 'Hungarian Forint',
    symbolNative: 'Ft',
    decimalDigits: 0,
    rounding: 0,
    code: 'HUF',
    namePlural: 'Hungarian forints',
  },
  IDR: {
    symbol: 'Rp',
    name: 'Indonesian Rupiah',
    symbolNative: 'Rp',
    decimalDigits: 0,
    rounding: 0,
    code: 'IDR',
    namePlural: 'Indonesian rupiahs',
  },
  ILS: {
    symbol: '₪',
    name: 'Israeli New Sheqel',
    symbolNative: '₪',
    decimalDigits: 2,
    rounding: 0,
    code: 'ILS',
    namePlural: 'Israeli new sheqels',
  },
  INR: {
    symbol: 'Rs',
    name: 'Indian Rupee',
    symbolNative: 'টকা',
    decimalDigits: 2,
    rounding: 0,
    code: 'INR',
    namePlural: 'Indian rupees',
  },
  ISK: {
    symbol: 'Ikr',
    name: 'Icelandic Króna',
    symbolNative: 'kr',
    decimalDigits: 0,
    rounding: 0,
    code: 'ISK',
    namePlural: 'Icelandic krónur',
  },
  JPY: {
    symbol: '¥',
    name: 'Japanese Yen',
    symbolNative: '￥',
    decimalDigits: 0,
    rounding: 0,
    code: 'JPY',
    namePlural: 'Japanese yen',
  },
  KRW: {
    symbol: '₩',
    name: 'South Korean Won',
    symbolNative: '₩',
    decimalDigits: 0,
    rounding: 0,
    code: 'KRW',
    namePlural: 'South Korean won',
  },
  MXN: {
    symbol: 'MX$',
    name: 'Mexican Peso',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'MXN',
    namePlural: 'Mexican pesos',
  },
  MYR: {
    symbol: 'RM',
    name: 'Malaysian Ringgit',
    symbolNative: 'RM',
    decimalDigits: 2,
    rounding: 0,
    code: 'MYR',
    namePlural: 'Malaysian ringgits',
  },
  NOK: {
    symbol: 'Nkr',
    name: 'Norwegian Krone',
    symbolNative: 'kr',
    decimalDigits: 2,
    rounding: 0,
    code: 'NOK',
    namePlural: 'Norwegian kroner',
  },
  NZD: {
    symbol: 'NZ$',
    name: 'New Zealand Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'NZD',
    namePlural: 'New Zealand dollars',
  },
  PHP: {
    symbol: '₱',
    name: 'Philippine Peso',
    symbolNative: '₱',
    decimalDigits: 2,
    rounding: 0,
    code: 'PHP',
    namePlural: 'Philippine pesos',
  },
  PLN: {
    symbol: 'zł',
    name: 'Polish Zloty',
    symbolNative: 'zł',
    decimalDigits: 2,
    rounding: 0,
    code: 'PLN',
    namePlural: 'Polish zlotys',
  },
  RON: {
    symbol: 'RON',
    name: 'Romanian Leu',
    symbolNative: 'RON',
    decimalDigits: 2,
    rounding: 0,
    code: 'RON',
    namePlural: 'Romanian lei',
  },
  SEK: {
    symbol: 'Skr',
    name: 'Swedish Krona',
    symbolNative: 'kr',
    decimalDigits: 2,
    rounding: 0,
    code: 'SEK',
    namePlural: 'Swedish kronor',
  },
  SGD: {
    symbol: 'S$',
    name: 'Singapore Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0,
    code: 'SGD',
    namePlural: 'Singapore dollars',
  },
  THB: {
    symbol: '฿',
    name: 'Thai Baht',
    symbolNative: '฿',
    decimalDigits: 2,
    rounding: 0,
    code: 'THB',
    namePlural: 'Thai baht',
  },
  TRY: {
    symbol: 'TL',
    name: 'Turkish Lira',
    symbolNative: 'TL',
    decimalDigits: 2,
    rounding: 0,
    code: 'TRY',
    namePlural: 'Turkish Lira',
  },
  ZAR: {
    symbol: 'R',
    name: 'South African Rand',
    symbolNative: 'R',
    decimalDigits: 2,
    rounding: 0,
    code: 'ZAR',
    namePlural: 'South African rand',
  },
};

/**
 * @type {Record<import("..").Currency, string>}
 */
const format = {
  USD: 'en-US',
  AUD: 'en-AU',
  EUR: 'en-DE',
  GBP: 'en-UK',
  INR: 'en-IN',
  BGN: 'bg',
  BRL: 'pt-BR',
  CAD: 'en-CA',
  CHF: 'gsw',
  CNY: 'zh',
  CZK: 'cs',
  DKK: 'da',
  HKD: 'en-cz',
  HUF: 'hu-HU',
  IDR: 'in',
  ILS: 'he-IL',
  ISK: 'is',
  JPY: 'ja',
  KRW: 'kr',
  MXN: 'es-mx',
  MYR: 'ms',
  NOK: 'no',
  NZD: 'en-NZ',
  PHP: 'ph',
  PLN: 'pl',
  RON: 'ro',
  SEK: 'sv',
  SGD: 'en-SG',
  THB: 'th',
  TRY: 'tr',
  ZAR: 'en-za',
};

/**
 * @param {import("..").Currency} currency
 * @returns {(amount: number) => string}
 */
const formatCurrency = (currency) => (amount) => {
  return new Intl.NumberFormat(format[currency], {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency,
  }).format(amount);
};

/**
 * @type {Record<import("..").RoyaltyCategories,string>}
 */
const RoyaltyCategoryLabels = {
  all: 'All',
  'by-game': 'By Game',
  'by-year': 'By Year',
  'by-publisher': 'By Publisher',
  'by-type': 'By Type',
};

/**
 * @param {import("..").RoyaltyEventForApp} r
 */
const royaltyToYear = (r) => DateTime.fromMillis(r.date).toFormat('yyyy');

/**
 * @param {import("..").RoyaltyEventForApp[][]} byYear
 */
const useTallyByYear = (byYear = []) => {
  return byYear.map((royalties) => royalties.reduce((t, r) => t + r.convertedAmount, 0));
};

/**
 * @param {import("..").RoyaltyEventForApp[][]} byType
 */
const useTallyByType = (byType = []) => {
  return byType.map((royalties) => ({
    value: royalties.reduce((t, r) => t + r.convertedAmount, 0),
    label: RoyaltyTypeLabels[royalties[0].type],
  }));
};

/**
 * e.g. Thursday 1 January 1970
 * @param {number} timestamp
 */
const formatFullDate = (timestamp) => {
  return DateTime.fromMillis(timestamp).toFormat('DDDD');
};

export { AudienceTypes, CurrencyData, FollowUpStatusToDaysDefaults, GamesListFilters, PlayTestAudienceDescriptions, PlayTestAudienceLabels, PlayTestMediumDescriptions, PlayTestMediumLabels, PlayTestMediumTypes, PlayTestMetadataComponentTypeDescriptions, PlayTestMetadataComponentTypeLabels, PlayTestMetadataComponentTypeTypes, PublisherGameSort, PublisherGameStatusDescription, PublisherGameStatusLabel, PublisherGameStatusLabelShort, RoyaltyCategoryLabels, RoyaltyTypeDescriptions, RoyaltyTypeLabels, RoyaltyTypes, StatusDescriptions, StatusLables, SupportedCurrencies, TopicTypeDescriptions, TopicTypeLabels, TopicTypes, byStatus, byStatusAndAlphabetically, daysFromNow, formatAge, formatContactLocation, formatCurrency, formatFollowUp, formatFollowUpWithDate, formatFullDate, formatPublisherStatus, getDaySubmitted, getNextAction, isInFuture, nextFollowUpUsingStatus, preferredCurrencySet, royaltyToYear, sectionGames, showAlarm, showContract, useTallyByType, useTallyByYear, validateAudienceTypes, validateCurrency, validatePlayTestMedium, validatePlayTestMetadataComponentType, validateRoyaltyType, validateTopicTypes };
